import { DateTime } from 'luxon';

export const MARKETPLACE_TIMEZONE = 'Europe/London';
export const AVAILABILITY_PREFERENCES = {
  workingStart: '09:00', workingEnd: '18:00', bufferMinutes: 60, maxShootingHours: 6,
};
const TYPICAL_BOOKING_MINUTES = 60;

export const calendarDateTime = (value) => value instanceof Date
  ? DateTime.fromJSDate(value, { zone: MARKETPLACE_TIMEZONE })
  : DateTime.fromISO(String(value).replace(' ', 'T'), { zone: MARKETPLACE_TIMEZONE });

export const buildAvailabilityEvents = (entries, preferences = AVAILABILITY_PREFERENCES) => entries.flatMap((entry) => {
  const start = calendarDateTime(entry.start_time);
  const end = calendarDateTime(entry.end_time);
  if (!start.isValid || !end.isValid || end <= start) throw new Error('Invalid availability interval');
  const allDay = Boolean(entry.all_day);
  const event = {
    id: String(entry.id),
    title: entry.title || (entry.type === 'booking' ? 'Booking' : 'Unavailable'),
    start: allDay ? start.toISODate() : start.toISO(),
    end: allDay ? end.toISODate() : end.toISO(),
    allDay,
    extendedProps: { availabilityId: entry.id, bookingId: entry.booking_id, type: entry.type, derived: false },
    ...(entry.type === 'buffer' ? { display: 'background', classNames: ['BufferEvent'] } : {}),
  };
  if (entry.type !== 'booking' || allDay || preferences.bufferMinutes <= 0) return [event];
  return [event, {
    id: `buffer-${entry.id}`, title: 'Buffer', start: end.toISO(),
    end: end.plus({ minutes: preferences.bufferMinutes }).toISO(),
    allDay: false, display: 'background', classNames: ['BufferEvent'],
    extendedProps: { type: 'buffer', derived: true, bookingId: entry.booking_id },
  }];
});

const mergeIntervals = (intervals) => {
  const merged = [];
  [...intervals].sort((a, b) => a[0] - b[0]).forEach(([start, end]) => {
    const previous = merged[merged.length - 1];
    if (previous && start <= previous[1]) previous[1] = Math.max(previous[1], end);
    else merged.push([start, end]);
  });
  return merged;
};

export const calculateDayStatuses = (events, rangeStart, rangeEnd, preferences = AVAILABILITY_PREFERENCES) => {
  const statuses = {};
  if (!rangeStart || !rangeEnd) return statuses;
  const intervals = events.map((event) => ({
    ...event, startMs: calendarDateTime(event.start).toMillis(), endMs: calendarDateTime(event.end).toMillis(),
  }));
  const [startHour, startMinute] = preferences.workingStart.split(':').map(Number);
  const [endHour, endMinute] = preferences.workingEnd.split(':').map(Number);
  const rangeFinish = calendarDateTime(rangeEnd);

  for (let day = calendarDateTime(rangeStart).startOf('day'); day < rangeFinish; day = day.plus({ days: 1 })) {
    const dayEnd = day.plus({ days: 1 });
    const date = day.toISODate();
    const overlapping = intervals.filter((event) => event.startMs < dayEnd.toMillis() && event.endMs > day.toMillis());
    if (overlapping.some((event) => event.allDay)) {
      statuses[date] = 'full';
      continue;
    }
    const workingStart = day.set({ hour: startHour, minute: startMinute }).toMillis();
    const workingEnd = day.set({ hour: endHour, minute: endMinute }).toMillis();
    const clip = (event) => [Math.max(workingStart, event.startMs), Math.min(workingEnd, event.endMs)];
    const blocks = mergeIntervals(overlapping.map(clip).filter(([start, end]) => end > start));
    if (!blocks.length) {
      statuses[date] = 'free';
      continue;
    }
    const shooting = mergeIntervals(overlapping.filter((event) => event.extendedProps.type === 'booking')
      .map(clip).filter(([start, end]) => end > start));
    const shootingMinutes = shooting.reduce((total, [start, end]) => total + (end - start) / 60000, 0);
    let previousEnd = workingStart;
    let hasGap = false;
    blocks.forEach(([start, end]) => {
      if ((start - previousEnd) / 60000 >= TYPICAL_BOOKING_MINUTES) hasGap = true;
      previousEnd = Math.max(previousEnd, end);
    });
    if ((workingEnd - previousEnd) / 60000 >= TYPICAL_BOOKING_MINUTES) hasGap = true;
    statuses[date] = shootingMinutes >= preferences.maxShootingHours * 60 || !hasGap ? 'full' : 'partial';
  }
  return statuses;
};
